import { UntypedFormArray, UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { Component, OnInit } from '@angular/core';
import { ErrorResponse } from '../../../models/error.model';
import { QuotesService } from '../../../services/quotes.service';
import { UtilsService } from '../../../services/utils.service';
import { GuarantiesService, TenantInitializationParams } from '../../../services/guaranties.service';
import { ActivatedRoute, Router } from '@angular/router';
import { generateQuotationCategories, Quote } from '../../../models/quote.model';
import { pairwise } from 'rxjs/operators';
import { TenantsService } from 'src/app/services/tenants.service';
import { Tenant } from '../../../models/tenant.model';
import { generateOwnerCategoriesV2 } from '../../../models/owner.model';
import { ID } from 'src/app/models/ID.model';
import { generatePropertyCategories } from 'src/app/models/property.model';
import { PersonType } from 'src/app/models/person-type.model';

@Component({
    selector: 'app-new-warranty',
    templateUrl: './new-warranty.component.html',
    styleUrls: ['./new-warranty.component.scss']
})
export class NewWarrantyComponent implements OnInit {

    generateOwnerCategories = generateOwnerCategoriesV2;
    generatePropertyCategories = generatePropertyCategories;
    generateQuotationCategories = generateQuotationCategories;

    quote?: Quote;

    tenantsFormArray = new UntypedFormArray([]);

    get tenantsControls() {
        return this.tenantsFormArray.controls as UntypedFormGroup[];
    }

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private guarantiesService: GuarantiesService,
        private quotesService: QuotesService,
        private tenantsService: TenantsService,
        public utilsService: UtilsService
    ) { }

    ngOnInit(): void {
        this.route.queryParams.subscribe(params => {
            if (params.quoteId) {
                this.getQuote(params.quoteId);
            }
        });
        this.addTenant();
    }

    getQuote(quoteId: ID) {
        this.quotesService.getQuoteById(quoteId).subscribe(quote => {
            this.quote = quote;
        }, (error: ErrorResponse) => this.utilsService.alert('Error al obtener datos de la cotización', error.error?.descripcion));
    }

    addTenant() {
        const FG = new UntypedFormGroup({
            tipoPersona: new UntypedFormControl('FISICA', [Validators.required]),
            tipoDocumento: new UntypedFormControl('DNI', [Validators.required]), // NUEVO
            availability: new UntypedFormControl('UNKNOWN', [control => control.value === 'UNKNOWN' ? { unknownAvailability: true } : undefined]),
            dni: new UntypedFormControl(undefined, []),
            pasaporte: new UntypedFormControl(undefined, []), // NUEVO
            nombre: new UntypedFormControl(undefined, []),
            apellido: new UntypedFormControl(undefined, []),
            celular: new UntypedFormControl(undefined, [Validators.required]),
            email: new UntypedFormControl(undefined, [Validators.email]),
            sexo: new UntypedFormControl('M', []),
            cuit: new UntypedFormControl(undefined, []),
            razonSocial: new UntypedFormControl(undefined, []),
        });
        // NUEVO: Listener para cambio de tipo de documento
        FG.controls.tipoDocumento.valueChanges.subscribe(newValue => {
            // Limpiar availability cuando cambia el tipo de documento
            FG.controls.availability.setValue('UNKNOWN', { emitEvent: false });

            // Limpiar TODOS los campos cuando cambia el tipo de documento
            if (newValue === 'DNI') {
                FG.patchValue({
                    pasaporte: undefined,
                    nombre: undefined,
                    apellido: undefined,
                    email: undefined,
                    celular: undefined,
                    sexo: 'M'
                }, { emitEvent: false });
            } else if (newValue === 'PASAPORTE') {
                FG.patchValue({
                    dni: undefined,
                    nombre: undefined,
                    apellido: undefined,
                    email: undefined,
                    celular: undefined,
                    sexo: 'M'
                }, { emitEvent: false });
            }

            // Actualizar validadores y estado de campos
            this.updateValidators(FG, FG.controls.tipoPersona.value);
            this.updateDisabled(FG);
        });

        // NUEVO: Listener para cambios en pasaporte
        FG.controls.pasaporte.valueChanges
            .pipe(pairwise())
            .subscribe(([prev, next]: [any, any]) => {
                if (prev === next) return;
                // this.resetForm(FG, FG.value.tipoPersona, null, null, next);
                this.updateDisabled(FG);
                // IMPORTANTE: Solo buscar si es válido (mínimo 6 caracteres)
                if (FG.controls.pasaporte.value && FG.controls.pasaporte.valid && FG.controls.pasaporte.value.length >= 6) {
                    this.checkAvailability('pasaporte', next, FG);
                }
            });
        this.tenantsFormArray.push(FG);
        this.updateDisabled(FG);
        this.updateValidators(FG, FG.controls.tipoPersona.value);
        FG.controls.tipoPersona.valueChanges.subscribe(newValue => {
            this.resetForm(FG, newValue);
            this.updateDisabled(FG);
            this.updateValidators(FG, newValue);

        });
        FG.controls.dni.valueChanges
            .pipe(pairwise())
            .subscribe(([prev, next]: [any, any]) => {
                if (prev === next) return;
                // Solo procesar si el tipo de documento es DNI
                if (FG.controls.tipoDocumento.value === 'DNI') {
                    // No resetear si availability ya es NOT_AVAILABLE
                    if (FG.controls.availability.value !== 'NOT_AVAILABLE') {
                        FG.controls.availability.setValue('UNKNOWN', { emitEvent: false });
                    }
                    this.updateDisabled(FG);
                    if (FG.controls.dni.value && FG.controls.dni.valid) {
                        this.checkAvailability('dni', next, FG);
                    }
                }
            });
        FG.controls.cuit.valueChanges
            .pipe(pairwise())
            .subscribe(([prev, next]: [any, any]) => {
                if (prev === next) return;
                this.resetForm(FG, FG.value.tipoPersona, null, next);
                this.updateDisabled(FG);
                if (FG.controls.cuit.value && FG.controls.cuit.valid) {
                    this.checkAvailability('cuit', next, FG);
                }
            });
        // AGREGAR el listener de pasaporte (después del listener de cuit):
        FG.controls.pasaporte.valueChanges
            .pipe(pairwise())
            .subscribe(([prev, next]: [any, any]) => {
                if (prev === next) return;
                // Solo procesar si el tipo de documento es PASAPORTE
                if (FG.controls.tipoDocumento.value === 'PASAPORTE') {
                    // No resetear si availability ya es NOT_AVAILABLE
                    if (FG.controls.availability.value !== 'NOT_AVAILABLE') {
                        FG.controls.availability.setValue('UNKNOWN', { emitEvent: false });
                    }
                    this.updateDisabled(FG);
                    if (FG.controls.pasaporte.value && FG.controls.pasaporte.valid && FG.controls.pasaporte.value.length >= 6) {
                        this.checkAvailability('pasaporte', next, FG);
                    }
                }
            });

    }

    updateDisabled(tenantFG: UntypedFormGroup) {
        const action = tenantFG.controls.availability.value === 'AVAILABLE' ? 'enable' : 'disable';
        const exceptions = [tenantFG.controls.tipoPersona, tenantFG.controls.dni, tenantFG.controls.cuit, tenantFG.controls.tipoDocumento, tenantFG.controls.pasaporte];
        this.utilsService.disableOrEnableFormGroupRecursive(tenantFG, action, exceptions);
        tenantFG.controls.tipoPersona.enable({ emitEvent: false });
        tenantFG.controls.tipoDocumento.enable({ emitEvent: false });
        tenantFG.controls.dni.enable();
        tenantFG.controls.cuit.enable();
        tenantFG.controls.pasaporte.enable();
    }

    updateValidators(tenantFG: UntypedFormGroup, tipoPersona: PersonType) {
        if (tipoPersona === 'JURIDICA') {
            tenantFG.controls.dni.setValidators([]);
            tenantFG.controls.pasaporte.setValidators([]); // NUEVO
            tenantFG.controls.tipoDocumento.setValidators([]); // NUEVO
            tenantFG.controls.nombre.setValidators([]);
            tenantFG.controls.apellido.setValidators([]);
            tenantFG.controls.sexo.setValidators([]);
            tenantFG.controls.cuit.setValidators([Validators.required, Validators.minLength(11), Validators.maxLength(11), this.utilsService.numberFormValidator]);
            tenantFG.controls.razonSocial.setValidators([Validators.required]);
        } else if (tipoPersona === 'FISICA') {
            const tipoDocumento = tenantFG.controls.tipoDocumento.value;

            // MODIFICADO: Validadores condicionales según tipo de documento
            if (tipoDocumento === 'DNI') {
                tenantFG.controls.dni.setValidators([Validators.required, Validators.minLength(8), Validators.maxLength(8), this.utilsService.numberFormValidator]);
                tenantFG.controls.pasaporte.setValidators([]);
            } else if (tipoDocumento === 'PASAPORTE') {
                tenantFG.controls.dni.setValidators([]);
                tenantFG.controls.pasaporte.setValidators([Validators.required, Validators.minLength(6), Validators.maxLength(20)]); // MODIFICADO: agregar minLength y maxLength
            }

            tenantFG.controls.tipoDocumento.setValidators([Validators.required]); // NUEVO
            tenantFG.controls.nombre.setValidators([Validators.required]);
            tenantFG.controls.apellido.setValidators([Validators.required]);
            tenantFG.controls.sexo.setValidators([Validators.required]);
            tenantFG.controls.cuit.setValidators([]);
            tenantFG.controls.razonSocial.setValidators([]);
        }
        tenantFG.updateValueAndValidity();
    }

    resetForm(tenantFG: UntypedFormGroup, tipoPersona: string, dni: string = null, cuit: string = null, pasaporte: string = null) { // MODIFICADO
        tenantFG.patchValue({
            availability: 'UNKNOWN',
            tipoPersona,
            tipoDocumento: dni ? 'DNI' : (pasaporte ? 'PASAPORTE' : 'DNI'), // NUEVO
            dni: dni ?? undefined,
            cuit: cuit ?? undefined,
            pasaporte: pasaporte ?? undefined, // NUEVO
            email: undefined,
            nombre: undefined,
            apellido: undefined,
            celular: undefined,
            sexo: 'M',
            razonSocial: undefined,
        }, { emitEvent: false });
    }

    checkAvailability(type: 'dni' | 'cuit' | 'pasaporte', identification: string, group: UntypedFormGroup) {
        this.tenantsService.checkAvailability(type, identification).subscribe(tenant => {
            if (tenant) {
                this.fillTenantForm(group, tenant, 'NOT_AVAILABLE', type); // AGREGAR el parámetro 'type'
            } else {
                group.controls.availability.setValue('AVAILABLE');
            }
            this.updateDisabled(group);
        }, (error: ErrorResponse) => this.utilsService.alert(`Error al consultar disponibilidad del ${type}`, error.error?.descripcion));
    }

    fillTenantForm(tenantFG: UntypedFormGroup, tenant: Tenant, availability: string, searchType?: 'dni' | 'cuit' | 'pasaporte') {
        // Determinar tipo de documento basándose en el tipo de búsqueda realizada
        let tipoDocumento: string;

        if (searchType === 'pasaporte') {
            tipoDocumento = 'PASAPORTE';
        } else if (searchType === 'dni') {
            tipoDocumento = 'DNI';
        } else {
            // Fallback: decidir basándose en los datos disponibles
            tipoDocumento = tenant.personaFisica?.dni ? 'DNI' :
                (tenant.personaFisica?.pasaporte ? 'PASAPORTE' : 'DNI');
        }

        tenantFG.patchValue({
            availability,
            tipoPersona: tenant.tipoPersona,
            tipoDocumento: tipoDocumento,
            dni: tenant.personaFisica?.dni || undefined,
            pasaporte: tenant.personaFisica?.pasaporte || undefined,
            cuit: tenant.personaJuridica?.cuit || undefined,
            nombre: tenant.personaFisica?.nombre || undefined,
            apellido: tenant.personaFisica?.apellido || undefined,
            email: tenant.email || undefined,
            celular: tenant.tipoPersona === 'FISICA' ? tenant.personaFisica?.celular : tenant.personaJuridica?.celular,
            sexo: tenant.personaFisica?.sexo || 'M',
            razonSocial: tenant.personaJuridica?.razonSocial || undefined,
        }, { emitEvent: false });
    }
    createWarranty() {
        this.tenantsFormArray.markAllAsTouched();
        if (this.tenantsFormArray.valid) {
            this.guarantiesService.newWarranty({
                idCotizacion: this.quote.id,
                inquilinos: this.tenantsFormArray.getRawValue().map(tenant => {
                    delete tenant.availability;
                    return tenant as TenantInitializationParams;
                })
            }).subscribe(warranty => {
                this.router.navigate(['garantias', warranty.id]);
            }, (error: ErrorResponse) => this.utilsService.alert(`Error al generar garantía`, error.error?.descripcion));
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }
}
